import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { House, Receipt, Users as UsersIcon, ChartLineUp, SignOut, BookOpen, IdentificationCard } from "@phosphor-icons/react";
import BookSelector from "@/components/BookSelector";

const Item = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 border-2 border-[#0f1b4c] font-bold uppercase tracking-wider text-sm transition-all ${
        isActive
          ? "bg-[#1e2a6b] text-white shadow-[4px_4px_0_#0f1b4c]"
          : "bg-white text-[#0f1b4c] hover:-translate-y-0.5 hover:-translate-x-0.5 hover:shadow-[4px_4px_0_#0f1b4c]"
      }`
    }
  >
    <Icon size={20} weight="bold" />
    <span>{label}</span>
  </NavLink>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen">
      <div className="brand-stripe" />
      {/* Top bar */}
      <header className="border-b-2 border-[#0f1b4c] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/jespay-logo.webp"
              alt="Jespay"
              className="h-14 w-auto"
              data-testid="brand-logo"
            />
            <div className="hidden sm:block border-l-2 border-[#0f1b4c]/20 pl-3">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#4a5578]">Private Limited</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#2cb4a6] font-bold">Office Ledger</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <BookSelector />
            <div className="hidden md:flex flex-col text-right">
              <span className="text-sm font-bold">{user?.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-[#4a5578]">{user?.role}</span>
            </div>
            <div className="w-10 h-10 border-2 border-[#0f1b4c] shadow-[3px_3px_0_#0f1b4c] bg-[#2cb4a6] text-white flex items-center justify-center font-black">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="brut-btn brut-btn-secondary flex items-center gap-2 text-sm"
            >
              <SignOut size={16} weight="bold" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Ticker */}
      <div className="border-b-2 border-[#0f1b4c] bg-[#0f1b4c] text-white overflow-hidden">
        <div className="ticker whitespace-nowrap py-1.5 text-xs font-mono-fin uppercase tracking-widest">
          {["a", "b", "c", "d", "e", "f"].map((k) => (
            <span key={`ticker-${k}`} className="mx-8">
              JESPAY · YOUR PAYMENTS, SIMPLIFIED · CASH · UPI · DTT · ATM · REAL-TIME BALANCE · INR ₹ · SECURED ·
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-6">
        <aside className="space-y-3">
          <Item to="/" icon={House} label="Dashboard" testid="nav-dashboard" />
          <Item to="/transactions" icon={Receipt} label="Ledger" testid="nav-transactions" />
          <Item to="/reports" icon={ChartLineUp} label="Reports" testid="nav-reports" />
          <Item to="/people" icon={IdentificationCard} label="People" testid="nav-people" />
          {user?.role === "admin" && (
            <Item to="/books" icon={BookOpen} label="Books" testid="nav-books" />
          )}
          {user?.role === "admin" && (
            <Item to="/users" icon={UsersIcon} label="Users" testid="nav-users" />
          )}
        </aside>
        <main>{children}</main>
      </div>

      <footer className="border-t-2 border-[#0f1b4c] mt-10 py-4 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 text-[10px] uppercase tracking-widest text-[#4a5578] flex justify-between">
          <span>© {new Date().getFullYear()} Jespay Systems Private Limited</span>
          <span>Internal use only · v1.1</span>
        </div>
      </footer>
    </div>
  );
}
